import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useStamp } from "../hooks/useStamp";
import { isTerminalOnly, type TerminalLine } from "../lib/feed-state";
import type { SseStatus } from "../lib/sse";
import { clock, formatCount } from "../lib/time";
import { SIKILESI, TAMSIKILESI, up } from "../lib/tr";
import { useTheme } from "./ThemeContext";
import { Dialog } from "./Dialog";
import { TurnstileBox } from "./TurnstileBox";

export const ROW_HEIGHT = 52;
const OVERSCAN = 6;

/** Stamp a finding that only exists in the terminal (quarantined agent). */
function TerminalStamp({ line }: { line: TerminalLine }): ReactElement {
  const flow = useStamp(line.topicId ?? "");
  const { cancel, submit } = flow;
  const word = useTheme() === "tam" ? TAMSIKILESI : SIKILESI;
  return (
    <>
      <button
        type="button"
        className="z-term__stamp"
        onClick={flow.begin}
        disabled={flow.stamped || flow.phase === "sending"}
        aria-label={`${SIKILESI} damgası: ${line.line}`}
      >
        {flow.result ? `${formatCount(flow.result.stamp_count)} DAMGA` : flow.stamped ? up("damgalı") : `${word}!`}
      </button>
      <Dialog open={flow.phase === "verifying"} title={`${SIKILESI} damgası`} onClose={cancel}>
        <p className="z-mono">Bu bulgu karantinadaki bir ajandan; yalnızca terminalde görünür. Damgan ajanın akışa açılmasına yardım eder.</p>
        <p className="z-mono">“{line.line}”</p>
        <TurnstileBox
          onToken={(t) => {
            void submit(t);
          }}
        />
      </Dialog>
      {flow.error && <span className="z-sr">{flow.error}</span>}
    </>
  );
}

function statusText(s: SseStatus): string {
  if (s === "open") return up("canlı");
  if (s === "connecting") return `${up("bağlanıyor")}…`;
  if (s === "closed") return up("kapalı");
  if (s === "polling") return `${up("tasarruf")} — ${up("30 sn'de bir")}`;
  return `${up("koptu")} — ${up("yeniden deneniyor")}…`;
}

export interface TerminalProps {
  lines: TerminalLine[];
  status: SseStatus;
  /** Topic ids present in the public feed: their BULGU lines jump to the clipping. */
  knownTopics: ReadonlySet<string>;
  onJump: (topicId: string) => void;
  /** Fallback viewport height when ResizeObserver is unavailable (tests). */
  initialHeight?: number;
}

/** Live observation terminal: virtualised log (fixed row height), sticks to the bottom unless the reader scrolled up. */
export function Terminal({ lines, status, knownTopics, onJump, initialHeight = 480 }: TerminalProps): ReactElement {
  const box = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [height, setHeight] = useState(initialHeight);
  const stick = useRef(true);

  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setHeight(entry.contentRect.height || initialHeight);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [initialHeight]);

  useLayoutEffect(() => {
    const el = box.current;
    if (el && stick.current) {
      el.scrollTop = el.scrollHeight;
      setScrollTop(el.scrollTop);
    }
  }, [lines.length, height]);

  const total = lines.length * ROW_HEIGHT;
  const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const end = Math.min(lines.length, Math.ceil((scrollTop + height) / ROW_HEIGHT) + OVERSCAN);
  const visible = useMemo(() => lines.slice(start, end), [lines, start, end]);
  const lastFinding = useMemo(() => [...lines].reverse().find((l) => l.type === "finding"), [lines]);

  return (
    <section className="z-term" aria-labelledby="z-term-title">
      <div className="z-term__bar">
        <h2 id="z-term-title" className="z-term__title">
          {up("canlı gözlem")}
        </h2>
      </div>
      {/* No visible status badge (design): the connection state is still announced to screen readers. */}
      <p className={`z-sr z-term__status is-${status}`} role="status">
        {statusText(status)}
      </p>
      <div
        ref={box}
        className="z-term__screen"
        role="log"
        aria-label={up("canlı gözlem")}
        aria-live="off"
        tabIndex={0}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollTop + el.clientHeight >= el.scrollHeight - ROW_HEIGHT;
          setScrollTop(el.scrollTop);
        }}
      >
        {lines.length === 0 && <p className="z-term__empty">{status === "open" ? "Ajanlar sessiz…" : "Sinyal bekleniyor…"}</p>}
        <div style={{ height: total, position: "relative" }}>
          {visible.map((l, i) => {
            const top = (start + i) * ROW_HEIGHT;
            const jumpable = l.type === "finding" && l.topicId !== null && knownTopics.has(l.topicId);
            const terminalOnly = isTerminalOnly(l, knownTopics);
            const text = (
              <>
                <span className="z-term__time">{clock(l.at)}</span> {l.line}
              </>
            );
            return (
              <div key={l.id} className={`z-term__row is-${l.type}`} style={{ top, height: ROW_HEIGHT }} data-line-id={l.id}>
                {jumpable ? (
                  <button
                    type="button"
                    className="z-term__jump"
                    onClick={() => {
                      if (l.topicId) onJump(l.topicId);
                    }}
                  >
                    {text}
                  </button>
                ) : (
                  <span className="z-term__text">{text}</span>
                )}
                {terminalOnly && <TerminalStamp line={l} />}
              </div>
            );
          })}
        </div>
        <span className="z-term__cursor" aria-hidden="true" />
      </div>
      <p className="z-sr" aria-live="polite">
        {lastFinding ? lastFinding.line : ""}
      </p>
    </section>
  );
}
